<script lang="ts">
  import CodeUpload from "../components/CodeUpload.svelte";
  import ReceiptUpload from "../components/ReceiptUpload.svelte";
  import { copy } from "../lib/stores/copy";
  import ArrowDeliverVertical from "../assets/arrow-deliver-vertical.svg";
  import UnlockSkin from "../assets/bo7-skin.webp";
  import { countryStore } from "../lib/stores/country";
  import { route, nextStep, setLoading, setPage } from "../lib/stores/route";
  import { saveCode } from "../lib/helpers/code";
  import { notify } from "../lib/helpers/bugsnag";
  import { isDemo } from "../lib/stores/mode";
  import { isPrimaryCampaign } from "../lib/constants/countries";

  let type: 'code' | 'receipt' | undefined = undefined;

  const setType = (newType: 'code' | 'receipt') => type = newType;

  const saveQueryCode = async () => {
    setLoading(true);
    const code = sessionStorage.getItem("code");

    if (code && !isDemo()) {
      try {
        await saveCode(code);
      } catch (error) {
        console.error("Failed to save query code:", error);
        notify({ errorMessage: "Failed to save query code", error });
        setPage("error"); // Probably doesn't matter having a distinct error as it shouldn't be likely to happen
      }
    } else if (code && isDemo()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    sessionStorage.removeItem("code");
    nextStep();
    setLoading(false);
  }

  if (!isPrimaryCampaign) {
    setType('receipt');
  } else if (sessionStorage.getItem("code")) {
    saveQueryCode();
  } else if (!type && $countryStore === 'US') {
    setType('code');
  }
</script>

<!-- We want to retain the data inputed, so we never actually remove -->
{#if !type}
  <main class="bg-cover bg-no-repeat bg-[center_top] bg-[url('/bg/mobile-bg7.webp')] tabletLg:bg-none flex-col grow relative bg-dark" class:hidden={$route.isLoading}>
    <div class="absolute w-full h-[70%] hidden tabletLg:block tabletLg:bg-[url('/bg/main-bg.webp')] bg-cover bg-no-repeat bg-center tabletLg:[mask:linear-gradient(to_bottom,black_0%,black_60%,transparent_100%)] tabletLg:[-webkit-mask:linear-gradient(to_bottom,black_0%,black_60%,transparent_100%)] z-[0]">
      <div class="items-center justify-center w-[30vw] ml-[-6%] flex absolute left-0 top-[15%] laptop:top-0">
        <img src={UnlockSkin} alt="" class="max-h-full object-contain">
      </div>
    </div>
    <div class="flex justify-center items-center flex-col grow max-w-[1820px] px-[16px] pb-[calc(118px+1rem)] tabletLg:pb-[42px] w-full mx-auto text-center z-[1] tablet:flex-row scale-90 mobileXS:scale-100 overflow-hidden">
      <div class="flex justify-center w-full tablet:w-auto">
        <div class="relative mt-[24px] p-4 z-[1] tabletLg:pt-[164px] bg-black/50 tabletLg:bg-transparent rounded-md">
          <div class="text-center tabletLg:p-4 tabletLg:bg-black/50 rounded-md mb-4 w-full tablet:w-auto">
            <h1 class="font-MrEavesXLSanNarOTUltra flex flex-col items-center text-primary whitespace-normal leading-[0.75] uppercase mb-[14px] tablet:block tablet:whitespace-nowrap laptop:mb-[20px] {$copy.purchaseSelection.titleTextClasses}"> <!-- [CHANGES-SM] text size classes moved to copy files -->
              {$copy.common.unlock} <span class="flex tablet:inline text-[0.85em] tablet:text-[1em]">{$copy.common.dual2xp}</span> 
            </h1>
            <div class="flex flex-col items-center">
              <div class="font-MrEavesXLSanNarOTUltra text-[19px] leading-[1] mb-0 uppercase text-white font-black tablet:text-[24px] laptop:text-[29px] desktop:text-[32px] desktopLg:text-[35px]">
                {$copy.purchaseSelection.subtitle}
              </div>
              <div class="font-MrEavesXLSanNarOT text-[18px] text-white text-center laptop:text-[15px] desktop:text-[18px] desktopLg:text-[21px]">
                {$copy.purchaseSelection.limits}
              </div>
            </div>
          </div>
          <div class="flex justify-center mt-[32px] tablet:mt-[64px]">
            <div class="unlock-action w-[132px] mobile:w-[160px] tablet:w-[204px] laptop:w-[256px] desktop:w-[320px] desktopLg:w-[400px]"> <!-- [CHANGES-SM] Applied static widths from the first child, removed the min-w queries: mobileS:min-w-[164px] mobile:min-w-[188px] -->
              <a
                href="/"
                on:click|preventDefault={() => setType("code")}
                class="unlock-action__link unlock-action__link--code relative flex items-center justify-center mx-auto text-white transition w-[132px] h-[132px] mobile:w-[160px] mobile:h-[160px] tablet:w-[204px] tablet:h-[204px] laptop:w-[256px] laptop:h-[256px] desktop:w-[320px] desktop:h-[320px] desktopLg:w-[400px] desktopLg:h-[400px]"
              >
                <span class="font-MrEavesXLSanNarOTUltra absolute bottom-[10%] text-[20px] uppercase mobile:text-[28px] tablet:text-[34px] laptop:text-[42px] desktop:text-[52px] desktopLg:text-[64px]">
                  {$copy.purchaseSelection.actionCodeSmall}
                </span>
              </a>
              <div class="unlock-action__descr mt-[12px] text-center tablet:mt-[24px]">
                <div class="unlock-action__subtitle font-MrEavesXLSanNarOTUltra text-[16px] leading-[1] mb-[16px] uppercase text-white font-black mobile:text-[19px] tablet:text-[24px] laptop:text-[29px] desktop:text-[32px] desktopLg:text-[40px]">
                  {$copy.purchaseSelection.actionCode}
                </div>
                <div class="unlock-action__text font-MrEavesXLSanNarOT text-[10px] leading-[1] text-white text-[14px] mobile:text-[15px] tablet:text-[16px] laptop:text-[17px] desktop:text-[22px] desktopLg:text-[28px]">
                  {$copy.purchaseSelection.actionCodeDescription}
                </div>
              </div>
            </div>
            <div class="mx-[20px] flex flex-col items-center justify-end"> <!-- [CHANGES-SM] Removed tablet:mx-[20px], made mx-[4px] 20px as well -->
              <div class="font-MrEavesXLSanNarOTUltra text-white uppercase mb-[28px] decoration-[3px] underline text-[22px] mobileS:text-[26px] mobile:text-[34px] mobileS:mb-[16px] mobile:mb-[38px] tablet:text-[34px] tablet:mb-[24px] laptop:text-[42px] laptop:mb-[30px] desktop:text-[52px] desktop:mb-[42px] desktopLg:text-[64px] desktopLg:mb-[30px]">
                {$copy.purchaseSelection.or}
              </div>
              <div class="max-w-[4.5px] mb-[-24px] w-full shrink-0 mobileS:max-w-[5px] tablet:max-w-[6.5px] laptop:max-w-[7.5px] desktop:max-w-[8.5px] desktopLg:max-w-[11px]">
                <img src={ArrowDeliverVertical} alt="" aria-hidden="true">
              </div>
            </div>
            <div class="unlock-action w-[132px] mobile:w-[160px] tablet:w-[204px] laptop:w-[256px] desktop:w-[320px] desktopLg:w-[400px]"> <!-- [CHANGES-SM] Applied static widths from the first child, removed the min-w queries: mobileS:min-w-[164px] mobile:min-w-[188px] -->
              <a
                href="/"
                on:click|preventDefault={() => setType("receipt")}
                class="unlock-action__link unlock-action__link--upload relative flex items-center justify-center mx-auto text-white transition transition w-[132px] h-[132px] mobile:w-[160px] mobile:h-[160px] tablet:w-[204px] tablet:h-[204px] laptop:w-[256px] laptop:h-[256px] desktop:w-[320px] desktop:h-[320px] desktopLg:w-[400px] desktopLg:h-[400px]"
              >
                <span class="font-MrEavesXLSanNarOTUltra absolute bottom-[10%] text-[20px] uppercase mobile:text-[28px] tablet:text-[34px] laptop:text-[42px] desktop:text-[52px] desktopLg:text-[64px]">
                  {$copy.purchaseSelection.actionUploadSmall}
                </span>
              </a>
              <div class="unlock-action__descr mt-[12px] text-center tablet:mt-[24px]">
                <div class="unlock-action__subtitle font-MrEavesXLSanNarOTUltra text-[16px] leading-[1] mb-[16px] uppercase text-white font-black mobile:text-[19px] tablet:text-[24px] laptop:text-[29px] desktop:text-[32px] desktopLg:text-[40px]">
                  {$copy.purchaseSelection.actionUpload}
                </div>
                <div class="unlock-action__text font-MrEavesXLSanNarOT text-[10px] leading-[1] text-white text-[14px] mobile:text-[15px] tablet:text-[16px] laptop:text-[17px] desktop:text-[22px] desktopLg:text-[28px]">
                  {$copy.purchaseSelection.actionUploadDescription}
                </div>
              </div>
            </div>
          </div>
          <div class="font-MrEavesXLSanNarOT leading-[1] text-[18px] text-white mt-[36px] text-center laptop:text-[15px] desktop:text-[18px] desktopLg:text-[21px]">
            {@html $copy.purchaseSelection.checkRules}
          </div>
        </div>
      </div>
    </div>
  </main>
{:else if type === 'code'}
  <CodeUpload toggleType={() => setType("receipt")} />
{:else if type === 'receipt'}
  <ReceiptUpload toggleType={() => setType("code")} {setType} />
{/if}
